import {view, Divider, tokens, Icon} from '@sail/ui';
import {payment} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useState} from 'react';
import {useWidget} from '../../../Widget/useWidget';
import {PaymentIntentCardStatusBadge} from './StatusBadges/PaymentIntentCardStatusBadge';

type PaymentIntentCardProps = {
  agentCallResponse: AgentCallResponse;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.payment_intent_card.title',
    description: 'Payment Intent Card title',
    defaultMessage: 'Payment',
  },
});

export const PaymentIntentCard = ({
  agentCallResponse,
}: PaymentIntentCardProps) => {
  const intl = useIntl();
  const widget = useWidget();
  const [isHovered, setIsHovered] = useState(false);

  const {payment_intent: paymentIntent, customer} = agentCallResponse;
  if (!paymentIntent) {
    return null;
  }

  const {id: paymentIntentId, amount, currency, status} = paymentIntent || {};
  const {name: customerName, email: customerEmail} = customer || {};

  const displayName = () => {
    if (customerName) {
      return customerName;
    }
    if (customerEmail) {
      return customerEmail;
    }
  };

  return (
    <view.div
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 1,
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/payments/${paymentIntentId}`,
          },
        });
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'small',
          width: '100%',
          minWidth: 0,
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: tokens.space[75].toString(),
            alignItems: 'center',
          }}
        >
          <Icon
            icon={payment}
            size="xsmall"
            css={{color: isHovered ? 'action.primary' : 'subdued'}}
          />
          <view.span
            css={{
              color: 'subdued',
              font: 'label.small.emphasized',
              flexShrink: 0,
            }}
          >
            {intl.formatMessage(messages.cardTitle)}
          </view.span>
          {displayName() ? (
            <view.div css={{height: 10, stack: 'x', width: 0}}>
              <Divider />
            </view.div>
          ) : null}
          {displayName() ? (
            <view.span
              css={{
                font: 'label.small.emphasized',
                color: 'action.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ':hover': {
                  color: 'action.primary.hovered',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                widget.requestAction({
                  name: 'navigate',
                  data: {
                    href: `/customers/${customer?.id}`,
                  },
                });
              }}
            >
              {displayName()}
            </view.span>
          ) : null}
        </view.div>
        <view.div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'small',
            width: '100%',
            minWidth: 0,
          }}
        >
          {amount && currency ? (
            <view.span
              css={{
                font: 'heading.large',
                flexShrink: 0,
              }}
            >
              {intl.formatNumber(amount / 100, {
                style: 'currency',
                currency,
              })}
            </view.span>
          ) : null}
          <view.div
            css={{
              minWidth: 0,
              flex: 1,
              marginTop: 'xxsmall',
            }}
          >
            <PaymentIntentCardStatusBadge status={status} />
          </view.div>
        </view.div>
      </view.div>
    </view.div>
  );
};
